import { IsolatedCartType, getBuyNowKey } from '@teko/cart/v2';
import {
  Button,
  CustomLink,
  Modal,
  ModalBody,
  ModalHeader,
  SubTitle,
  styled,
  useToast,
} from '@teko/ds-desktop';
import { CautionOutlined } from '@teko/ds-icon';
import { logger } from '@teko/log';
import { formatCurrency } from '@teko/teko-ecommerce';
import {
  AddToCartType,
  TrackAddToCartParams,
  getTrackingData,
  trackAddToCart,
} from '@teko/tracking';
import { useCartV2, useProductDetailState, useTenantConfig } from 'contexts';
import { t } from 'i18n';
import { useRouter } from 'next/router';
import React, { useMemo } from 'react';

export const ActionButtons: React.FC = () => {
  const router = useRouter();
  const {
    product,
    primitiveProduct,
    isOutOfStock,
    isCanAddToCard,
    stockAvailable,
    price,
    payloadAddToCart,
    selectedPromotion,
    actionReady,
    isAllowInstallmentPayment,
    selectedPromotionsNotApplyWithInstallment,
    showWarningPromotionWithInstallment,
    setShowWarningPromotionWithInstallment,
    minAmountByMonth,
  } = useProductDetailState();

  const { contactLink, allowBuyNow, epicOmni1459 } = useTenantConfig();
  const showToast = useToast();
  const { onAddSingleItem } = useCartV2();
  const isValidPrice = useMemo(() => {
    if (!product) return false;
    if (price) return price?.finalPrice && price?.finalPrice >= 0;
  }, [product, price]);
  const showContact =
    !isValidPrice || isOutOfStock || !product?.status?.sellable;

  const trackingPayload = JSON.stringify({
    sku: product?.sellerSku,
    screenName: 'productDetail',
  });

  const onAddItemToCart = async (type: AddToCartType) => {
    if (!isCanAddToCard) {
      showToast.info?.({
        title: '',
        message: t('Chỉ còn {{stockAvailable}} sản phẩm', {
          stockAvailable: stockAvailable,
        }),
        type: 'error',
        icon: <CautionOutlined size={50} color="#E24C4B" />,
      });
      return;
    }
    if (!primitiveProduct) return;

    // params send to Teko tracking in event addToCart
    const trackAddToCartParams: TrackAddToCartParams = {
      product: {
        sku: product?.sellerSku || '',
        name: product?.name || '',
        brand: product?.brand,
        categories: product?.categories,
        price: parseFloat(
          product?.price?.supplierRetailPrice?.toString() || '0',
        ),
        addToCartPrice: price?.finalPrice || 0,
        sellerSku: product?.sellerSku,
      },
      selectedPromotionId: selectedPromotion?.promotionId,
      type,
    };

    try {
      if (!payloadAddToCart?.sku) return;

      const res = await onAddSingleItem(
        {
          sku: payloadAddToCart?.sku,
          promotionId: selectedPromotion?.promotionId,
          quantity: payloadAddToCart?.quantity,
          isPrimary: payloadAddToCart?.isPrimary,
        },
        false,
        allowBuyNow && type === AddToCartType.BUYNOW
          ? IsolatedCartType.BUY_NOW
          : epicOmni1459 && type === AddToCartType.INSTALLMENT
          ? IsolatedCartType.INSTALLMENT
          : undefined,
      );
      if (res) {
        trackAddToCart({
          ...trackAddToCartParams,
          status: 'success',
          cartId: res.id,
        });
      } else {
        trackAddToCart({ ...trackAddToCartParams, status: 'failed' });
      }

      if (
        res &&
        (type === AddToCartType.BUYNOW || type === AddToCartType.INSTALLMENT)
      ) {
        if (type === AddToCartType.BUYNOW && !allowBuyNow) {
          router.push('/cart');
        } else {
          const key = getBuyNowKey(true);
          router.push(`/checkout?bnk=${key}`);
        }
      }
    } catch (e) {
      trackAddToCart({ ...trackAddToCartParams, status: 'failed' });
      logger.error(e);
    }
  };

  const handlePayOnInstallment = () => {
    if (selectedPromotionsNotApplyWithInstallment.length > 0) {
      return setShowWarningPromotionWithInstallment(true);
    }
    return onAddItemToCart(AddToCartType.INSTALLMENT);
  };

  return (
    <>
      {!showContact &&
        (isAllowInstallmentPayment ? (
          <>
            <div style={{ width: '100%' }}>
              <StyledButton
                variant="primary"
                block
                marginTop="s16"
                onClick={() => onAddItemToCart(AddToCartType.BUYNOW)}
                disabled={!actionReady}
              >
                <ButtonText level={2} weight="medium">
                  {t('Mua ngay').toUpperCase()}
                </ButtonText>
              </StyledButton>
            </div>
            <ButtonWrapper>
              <AddToCartButtonWrapper
                {...getTrackingData({
                  isTrackContent: true,
                  contentName: 'addToCart',
                  target: 'productDetail',
                  regionName: 'descriptionDetail',
                  payload: trackingPayload,
                })}
              >
                <StyledButton
                  variant="secondary"
                  onClick={() => onAddItemToCart(AddToCartType.ADDTOCART)}
                  block
                  disabled={!actionReady}
                  height="57px"
                >
                  <ButtonText level={2} weight="medium">
                    {t('THÊM VÀO GIỎ HÀNG')}
                  </ButtonText>
                </StyledButton>
              </AddToCartButtonWrapper>
              <BuyNowButtonWrapper
                {...getTrackingData({
                  isTrackContent: true,
                  contentName: 'buyNow',
                  regionName: 'descriptionDetail',
                  target: 'cart',
                  payload: trackingPayload,
                })}
              >
                <StyledButton
                  variant="primary"
                  onClick={handlePayOnInstallment}
                  className="att-detail-page-buy-now-button"
                  block
                  disabled={!actionReady}
                  backgroundColor="#F89E33"
                  height="57px"
                >
                  <ButtonText level={2} weight="medium">
                    {t('Mua trả góp').toUpperCase()}
                    {typeof minAmountByMonth === 'number' && (
                      <div>
                        {`${t('Chỉ từ')} ${formatCurrency(
                          minAmountByMonth,
                        )}/${t('tháng')}`}
                      </div>
                    )}
                  </ButtonText>
                </StyledButton>
              </BuyNowButtonWrapper>
            </ButtonWrapper>
          </>
        ) : (
          <ButtonWrapper>
            <BuyNowButtonWrapper
              {...getTrackingData({
                isTrackContent: true,
                contentName: 'buyNow',
                regionName: 'descriptionDetail',
                target: 'cart',
                payload: trackingPayload,
              })}
            >
              <StyledButton
                variant="primary"
                onClick={() => onAddItemToCart(AddToCartType.BUYNOW)}
                className="att-detail-page-buy-now-button"
                block
                disabled={!actionReady}
              >
                <ButtonText level={2} weight="medium">
                  {t('Mua ngay').toUpperCase()}
                </ButtonText>
              </StyledButton>
            </BuyNowButtonWrapper>

            <AddToCartButtonWrapper
              {...getTrackingData({
                isTrackContent: true,
                contentName: 'addToCart',
                target: 'productDetail',
                regionName: 'descriptionDetail',
                payload: trackingPayload,
              })}
            >
              <StyledButton
                variant="secondary"
                onClick={() => onAddItemToCart(AddToCartType.ADDTOCART)}
                block
                disabled={!actionReady}
              >
                <ButtonText level={2} weight="medium">
                  {t('THÊM VÀO GIỎ HÀNG')}
                </ButtonText>
              </StyledButton>
            </AddToCartButtonWrapper>
          </ButtonWrapper>
        ))}
      {showContact && !!contactLink && (
        <CustomLink href={contactLink} target="_blank">
          <ContactSaleButtonWrapper
            {...getTrackingData({
              isTrackContent: true,
              regionName: 'descriptionDetail',
              contentName: 'contactSale',
              target: 'productDetail',
              payload: trackingPayload,
            })}
          >
            <StyledButton variant="primary" block marginTop="s16">
              <ButtonText level={2} weight="medium">
                {t('LIÊN HỆ')}
              </ButtonText>
            </StyledButton>
          </ContactSaleButtonWrapper>
        </CustomLink>
      )}
      {showContact && !contactLink && (
        <StyledButton disabled block marginTop="s16">
          <ButtonText level={2} weight="medium">
            {t('HẾT HÀNG')}
          </ButtonText>
        </StyledButton>
      )}
      <Modal
        visible={showWarningPromotionWithInstallment}
        size="s"
        cancelText={t('Quay lại')}
        onCancel={() => setShowWarningPromotionWithInstallment(false)}
        okText={t('Tiến hành trả góp')}
        onOk={() => {
          setShowWarningPromotionWithInstallment(false);
          onAddItemToCart(AddToCartType.INSTALLMENT);
        }}
        showCloseIcon={false}
      >
        <ModalHeader padding="s0">{t('Thông báo')}</ModalHeader>
        <ModalBody paddingLeft="s0" paddingRight="s0">
          {t(
            'Sản phẩm có khuyến mại không được áp dụng khi mua trả góp. Vui lòng chọn lại khuyến mại khác hoặc tiến hành trả góp mà không áp dụng khuyến mại',
          )}
        </ModalBody>
      </Modal>
    </>
  );
};
const ButtonWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
`;
const ButtonText = styled(SubTitle)`
  font-size: 14px;
`;
const StyledButton = styled(Button)(
  ({ height }: { height?: string }) => `
  height: ${height || '46px'};
`,
);

const BuyNowButtonWrapper = styled.div`
  flex: 1;
`;

const AddToCartButtonWrapper = styled.div`
  flex: 1;
`;

const ContactSaleButtonWrapper = styled.div``;
